import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ImagePlus, Check, UploadCloud, Loader2, X, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { analyze, MAX, EXT } from "@/pages/dashboard/Uploader";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { UPLOAD_GENRES, SUBTAGS } from "@/lib/hubs";
import { toast } from "sonner";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { CollectionCard } from "@/components/Collections";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Header } from "@/pages/dashboard/Insights";

const StagedTrack = ({ st, i, onChange, onRemove }) => (
  <div data-testid={`staged-track-${i}`} className="p-3 rounded-xl border border-white/10 bg-white/[0.02] space-y-2">
    <div className="flex items-center gap-2">
      <span className="w-6 h-6 shrink-0 rounded-full border border-white/20 flex items-center justify-center font-mono text-xs">{i + 1}</span>
      <Input data-testid={`staged-title-${i}`} value={st.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="Track title" className="h-9 bg-white/5 border-white/10" />
      <span className="hidden sm:inline text-[10px] font-mono text-zinc-500 shrink-0">{(st.file.size / 1048576).toFixed(1)}MB{st.duration ? ` · ${Math.round(st.duration)}s` : ""}</span>
      {st.status === "done" ? <Check size={16} className="text-emerald-400 shrink-0" /> : st.status === "uploading" ? <Loader2 size={16} className="animate-spin shrink-0" />
        : <button data-testid={`staged-remove-${i}`} onClick={onRemove} className="text-zinc-500 hover:text-red-400 shrink-0" aria-label="Remove"><X size={16} /></button>}
    </div>
    <div className="flex flex-wrap gap-1.5">{UPLOAD_GENRES.map((h) => {
      const on = st.genres.includes(h.key), full = st.genres.length >= 2 && !on;
      return <button key={h.key} type="button" data-testid={`staged-genre-${i}-${h.key}`} disabled={full} onClick={() => onChange({ genres: on ? st.genres.filter((g) => g !== h.key) : [...st.genres, h.key] })}
        className={`px-2 h-7 rounded-full text-[11px] border disabled:opacity-30 ${on ? "text-black border-transparent" : "border-white/10 hover:border-white/30"}`} style={on ? { background: h.color } : {}}>{h.name}</button>;
    })}</div>
    <div className="flex flex-col sm:flex-row gap-2">
      <select data-testid={`staged-subtag-${i}`} value={st.subtag} onChange={(e) => onChange({ subtag: e.target.value })} className="h-9 rounded-md px-2 bg-white/5 border border-white/10 text-xs">
        <option value="" className="bg-zinc-900">Sub-genre / vocal (optional)</option>
        {SUBTAGS.map((x) => <option key={x} value={x} className="bg-zinc-900">{x}</option>)}
      </select>
      <Input data-testid={`staged-credits-${i}`} value={st.credits} onChange={(e) => onChange({ credits: e.target.value })} placeholder="Credits (optional)" className="h-9 bg-white/5 border-white/10 text-xs" />
    </div>
  </div>
);

const UpgradeModal = ({ open, onOpenChange }) => {
  const nav = useNavigate();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="release-upgrade-modal" className="bg-[#12121A] border-white/10">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Lock size={20} className="text-[var(--hub)]" /> Upgrade to release an Album or EP</DialogTitle>
          <DialogDescription>Album and EP releases are available on Rising Star and Elite Label. Starter accounts can still publish singles and individual tracks.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <button data-testid="release-upgrade-rising" onClick={() => nav("/pricing")} className="h-12 rounded-full bg-[var(--hub)] text-white font-semibold">Rising Star</button>
          <button data-testid="release-upgrade-elite" onClick={() => nav("/pricing")} className="h-12 rounded-full bg-white text-black font-semibold">Elite Label</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EMPTY_ALBUM = { title: "", type: "ep", description: "", cover_url: null, track_ids: [] };

const AlbumForm = ({ onCreated }) => {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [f, setF] = useState(EMPTY_ALBUM);
  const [staged, setStaged] = useState([]);
  const [rights, setRights] = useState(false);
  const [busy, setBusy] = useState(false);
  const [upgrade, setUpgrade] = useState(false);
  const loadTracks = () => api.get("/me/tracks").then((r) => setTracks(r.data.filter((t) => t.status !== "deleted")));
  useEffect(() => { loadTracks(); }, []);
  const toggle = (id) => setF((s) => ({ ...s, track_ids: s.track_ids.includes(id) ? s.track_ids.filter((x) => x !== id) : [...s.track_ids, id] }));
  const locked = ["album", "ep"].includes(f.type) && !["rising", "elite"].includes(user?.plan) && user?.role !== "admin";
  const cover = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "album");
    try { const { data } = await api.post("/uploads/image", fd); setF((s) => ({ ...s, cover_url: data.url })); } catch (e) { toast.error(errMsg(e)); }
  };
  const addFiles = async (list) => {
    const files = [...list].filter((fl) => {
      const ok = EXT.includes(fl.name.split(".").pop().toLowerCase()) && fl.size <= MAX;
      if (!ok) toast.error(`${fl.name}: unsupported type or over 200MB`);
      return ok;
    });
    const items = files.map((file) => ({ key: `${file.name}-${file.size}-${Math.random()}`, file, title: file.name.replace(/\.[^.]+$/, ""), genres: [], subtag: "", credits: "", peaks: [], duration: 0, status: "ready" }));
    setStaged((s) => [...s, ...items]);
    for (const it of items) {
      const m = await analyze(it.file);
      setStaged((s) => s.map((x) => (x.key === it.key ? { ...x, ...m } : x)));
    }
  };
  const patchStaged = (key, upd) => setStaged((s) => s.map((x) => (x.key === key ? { ...x, ...upd } : x)));
  const pending = staged.filter((s) => s.status !== "done");
  const stagedValid = pending.every((s) => s.title.trim() && s.genres.length >= 1);
  const total = f.track_ids.length + staged.length;
  const submit = async () => {
    if (locked) return setUpgrade(true);
    setBusy(true);
    const newIds = staged.filter((s) => s.status === "done").map((s) => s.track_id);
    try {
      for (const st of pending) {
        patchStaged(st.key, { status: "uploading" });
        const fd = new FormData();
        fd.append("file", st.file); fd.append("title", st.title.trim()); fd.append("genres", st.genres.join(","));
        fd.append("subtag", st.subtag); fd.append("credits", st.credits); fd.append("description", "");
        fd.append("privacy", "public"); fd.append("women_hub", false); fd.append("copyright_confirmed", rights);
        fd.append("peaks", JSON.stringify(st.peaks)); fd.append("client_duration", st.duration || 0);
        try {
          const { data } = await api.post("/tracks/upload", fd);
          if (f.cover_url) await api.patch(`/tracks/${data.track_id}`, { cover_url: f.cover_url });
          newIds.push(data.track_id);
          patchStaged(st.key, { status: "done", track_id: data.track_id });
        } catch (e) {
          patchStaged(st.key, { status: "ready" });
          throw e;
        }
      }
      await api.post("/albums", { ...f, track_ids: [...f.track_ids, ...newIds] });
      toast.success("Release published");
      setF(EMPTY_ALBUM); setStaged([]); setRights(false);
      loadTracks(); onCreated();
    } catch (e) {
      if (e.response?.status === 402 && String(e.config?.url).includes("/albums")) setUpgrade(true);
      else toast.error(errMsg(e));
      loadTracks();
    } finally { setBusy(false); }
  };
  return (
    <div className="surface p-6 grid md:grid-cols-[180px_1fr] gap-6" data-testid="album-form">
      <label data-testid="album-cover-upload" className="w-40 md:w-auto aspect-square rounded-2xl border border-dashed border-white/15 overflow-hidden cursor-pointer hover:border-white/30">
        <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => e.target.files[0] && cover(e.target.files[0])} />
        {f.cover_url ? <img src={mediaUrl(f.cover_url)} alt="" className="w-full h-full object-cover" /> : <div className="h-full flex flex-col items-center justify-center gap-2 text-xs text-zinc-500"><ImagePlus size={18} />Cover art</div>}
      </label>
      <div className="space-y-3 min-w-0">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input data-testid="album-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Release title" className="h-11 bg-white/5 border-white/10" />
          <div className="self-start inline-flex p-1 rounded-full bg-white/5 border border-white/10 shrink-0">
            {["album", "ep", "single"].map((t) => <button key={t} data-testid={`album-type-${t}`} onClick={() => setF({ ...f, type: t })} className={`px-3 h-8 rounded-full text-xs uppercase font-mono ${f.type === t ? "bg-white text-black" : "text-zinc-400"}`}>{t}</button>)}
          </div>
        </div>
        <Textarea data-testid="album-description-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Liner notes, credits…" rows={2} className="bg-white/5 border-white/10" />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="eyebrow">Tracklist · {total} track{total === 1 ? "" : "s"}</div>
          <label data-testid="album-upload-track-button" className="h-9 px-4 rounded-full border border-white/15 text-sm inline-flex items-center gap-2 hover:border-white/40 cursor-pointer">
            <UploadCloud size={14} /> Add audio files
            <input data-testid="album-files-input" type="file" multiple accept={EXT.map((e) => "." + e).join(",")} hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          </label>
        </div>
        {staged.length > 0 && (
          <div className="space-y-2" data-testid="staged-tracks">
            <div className="text-xs text-zinc-500">New tracks — rename and edit details before publishing. Each needs at least 1 genre (max 2).</div>
            {staged.map((st, i) => <StagedTrack key={st.key} st={st} i={i} onChange={(u) => patchStaged(st.key, u)} onRemove={() => setStaged((s) => s.filter((x) => x.key !== st.key))} />)}
          </div>
        )}
        {tracks.length > 0 && <div className="text-xs text-zinc-500 pt-1">Or include tracks you already published:</div>}
        <div className="max-h-56 overflow-auto space-y-1">
          {tracks.map((t) => {
            const pos = f.track_ids.indexOf(t.track_id);
            return (
              <button key={t.track_id} data-testid={`album-track-option-${t.track_id}`} onClick={() => toggle(t.track_id)} className={`w-full flex items-center gap-3 p-2 rounded-lg text-left text-sm border ${pos >= 0 ? "border-[var(--hub)] bg-[var(--hub)]/10" : "border-transparent hover:bg-white/5"}`}>
                <span className="w-6 h-6 rounded-full border border-white/20 flex items-center justify-center font-mono text-xs">{pos >= 0 ? pos + 1 : ""}</span>
                <span className="flex-1 truncate">{t.title}</span>{pos >= 0 && <Check size={14} />}
              </button>
            );
          })}
        </div>
        {pending.length > 0 && (
          <label className="flex gap-3 items-start text-sm text-zinc-300 p-4 rounded-xl border border-[var(--hub)]/40 bg-[var(--hub)]/5">
            <Checkbox data-testid="album-rights-checkbox" checked={rights} onCheckedChange={(v) => setRights(!!v)} className="mt-0.5" />
            <span><b>Required.</b> I confirm I hold 100% of the master and publishing rights to every new recording in this release (or all necessary licences), and I accept the Terms of Service.</span>
          </label>
        )}
        <button data-testid="album-create-button" disabled={busy || !f.title.trim() || !total || !stagedValid || (pending.length > 0 && !rights)} onClick={submit}
          className="h-11 px-6 rounded-full bg-[var(--hub)] text-white font-semibold disabled:opacity-40 inline-flex items-center gap-2">
          {busy ? <Loader2 size={16} className="animate-spin" /> : locked ? <Lock size={16} /> : <Plus size={16} />} {busy ? "Publishing…" : "Publish release"}
        </button>
      </div>
      <UpgradeModal open={upgrade} onOpenChange={setUpgrade} />
    </div>
  );
};

export const Albums = () => {
  const [albums, setAlbums] = useState([]);
  const load = () => api.get("/me/albums").then((r) => setAlbums(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="albums-tab">
      <Header eyebrow="Releases" title="Albums & EPs" />
      <AlbumForm onCreated={load} />
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-5 mt-8" data-testid="my-albums-grid">
        {albums.map((a) => <CollectionCard key={a.album_id} item={a} kind="album" />)}
      </div>
    </div>
  );
};

export const Playlists = () => {
  const [lists, setLists] = useState([]);
  const [title, setTitle] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const load = () => api.get("/me/playlists").then((r) => setLists(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/playlists", { title, is_public: isPublic }); setTitle(""); toast.success("Playlist created"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="playlists-tab">
      <Header eyebrow="Library" title="My playlists" />
      <div className="surface p-5 flex flex-col sm:flex-row gap-3">
        <Input data-testid="playlist-title-input" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && title.trim() && create()} placeholder="Name your playlist" className="h-11 bg-white/5 border-white/10" />
        <div className="inline-flex p-1 rounded-full bg-white/5 border border-white/10 shrink-0">
          {[[true, "Public"], [false, "Private"]].map(([v, l]) => <button key={l} data-testid={`playlist-visibility-${l.toLowerCase()}`} onClick={() => setIsPublic(v)} className={`px-4 h-9 rounded-full text-sm ${isPublic === v ? "bg-white text-black" : "text-zinc-400"}`}>{l}</button>)}
        </div>
        <button data-testid="playlist-create-button" disabled={!title.trim()} onClick={create} className="h-11 px-6 rounded-full bg-[var(--hub)] text-white font-semibold disabled:opacity-40 inline-flex items-center gap-2 shrink-0"><Plus size={16} /> Create</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-5 mt-8" data-testid="my-playlists-grid">
        {lists.length === 0 && <div className="col-span-full text-zinc-500 text-sm">No playlists yet. Tip: tap the + list icon in the player to save what's playing.</div>}
        {lists.map((p) => <CollectionCard key={p.playlist_id} item={p} kind="playlist" />)}
      </div>
    </div>
  );
};
